import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccounts } from '@modules-deprecated/app/accounts'
import type { Account, AccountNature } from '@modules-deprecated/app/accounts/types'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useForm } from '../../../../../../../../../hooks'
import type { CreateAccountFromTemplateFormSchema } from '../../../types'
import {
  getAccountById,
  getAccountNumberFromAccount,
  getExcludedAccountIds,
} from '../../../utils/accountFromTemplateAccounts'
import { getCreateAccountFromTemplateDefaultValues } from '../../../utils/getCreateAccountFromTemplateDefaultValues'
import { getCreateAccountFromTemplateFormSchema } from '../../../utils/getCreateAccountFromTemplateFormSchema'
import { useAccountNumberField } from './useAccountNumberField'
import { useCreateAccountFromTemplateSubmission } from './useCreateAccountFromTemplateSubmission'

interface UseCreateAccountFromTemplateModalOptions {
  natures?: AccountNature[]
  onClose?: () => void
  onSave?: (account: Account) => void
}

export const useCreateAccountFromTemplateModal = ({
  natures,
  onClose,
  onSave,
}: UseCreateAccountFromTemplateModalOptions) => {
  const { t } = useTranslation()
  const { accounts } = useAccounts()
  const { organization } = useUserOrganization()
  const createAccountFromTemplateForm = useForm<CreateAccountFromTemplateFormSchema>({
    defaultValues: getCreateAccountFromTemplateDefaultValues(),
    mode: 'onChange',
    validationSchema: getCreateAccountFromTemplateFormSchema(t),
  })
  const { clearErrors, formState, Form, getValues, handleSubmit, reset, setError, setValue, watch } =
    createAccountFromTemplateForm

  const accountNumber = watch('accountNumber')
  const baseAccountId = watch('baseAccountId')
  const name = watch('name')
  const excludedAccountIds = useMemo(() => getExcludedAccountIds(accounts ?? []), [accounts])
  const selectedBaseAccount = useMemo(() => getAccountById(accounts ?? [], baseAccountId), [accounts, baseAccountId])
  const predefinedAccountId = selectedBaseAccount?.predefinedAccount?.id ?? ''

  const clearAccountNumberError = useCallback(() => clearErrors('accountNumber'), [clearErrors])

  const setAccountNumber = useCallback(
    (nextAccountNumber: number | null) => setValue('accountNumber', nextAccountNumber),
    [setValue],
  )

  const setAccountNumberError = useCallback(
    (message: string) => setError('accountNumber', { message, type: 'validate' }),
    [setError],
  )

  const getCurrentAccountNumber = useCallback(() => getValues('accountNumber'), [getValues])

  const {
    accountNumberCaption,
    handleAccountNumberBlur,
    handleAccountNumberChange,
    handleManualEntryChange,
    isFetchingAccountNumber,
    isManualEntryEnabled,
    isValidating,
    refreshAccountNumber,
    reset: resetAccountNumberField,
    selectBaseAccount,
    validateAccountNumberIfNeeded,
  } = useAccountNumberField({
    clearAccountNumberError,
    getAccountNumber: getCurrentAccountNumber,
    organizationId: organization?.id,
    predefinedAccountId,
    setAccountNumber,
    setAccountNumberError,
  })

  const resetModal = useCallback(() => {
    reset(getCreateAccountFromTemplateDefaultValues())
    resetAccountNumberField()
  }, [reset, resetAccountNumberField])

  const {
    accountNumberToConfirm,
    cancelConfirmation,
    closeModal,
    confirmCreate,
    isAwaitingConfirmation,
    isProcessing: isCreating,
    requestConfirmation,
  } = useCreateAccountFromTemplateSubmission({
    onAccountNumberConflict: refreshAccountNumber,
    onAccountNumberError: setAccountNumberError,
    onClose,
    onReset: resetModal,
    onSave,
    organizationId: organization?.id,
  })

  const handleBaseAccountSelect = useCallback(
    (id: string | undefined, account: Account | undefined) => {
      setValue('baseAccountId', id ?? '', {
        shouldDirty: true,
        shouldValidate: true,
      })
      selectBaseAccount(getAccountNumberFromAccount(account))
    },
    [selectBaseAccount, setValue],
  )

  const submitForm = useCallback(() => {
    handleSubmit(async (values) => {
      const { accountNumber: submittedAccountNumber, description, name: submittedName } = values

      if (!organization?.id || !predefinedAccountId || submittedAccountNumber === null) {
        return
      }

      const isAccountNumberAvailable = await validateAccountNumberIfNeeded(submittedAccountNumber)
      if (!isAccountNumberAvailable) {
        return
      }

      requestConfirmation({
        accountNumber: submittedAccountNumber,
        description,
        name: submittedName,
        predefinedAccountId,
      })
    })()
  }, [handleSubmit, organization?.id, predefinedAccountId, requestConfirmation, validateAccountNumberIfNeeded])

  const isProcessing = isCreating || isFetchingAccountNumber || isValidating || formState.isSubmitting
  const isSaveDisabled =
    !organization?.id ||
    !baseAccountId ||
    !predefinedAccountId ||
    !name.trim() ||
    accountNumber === null ||
    Boolean(formState.errors.accountNumber) ||
    isProcessing

  return {
    accountNumberCaption,
    accountNumberToConfirm,
    cancelConfirmation,
    closeModal,
    confirmCreate,
    excludedAccountIds,
    Form,
    handleAccountNumberBlur,
    handleAccountNumberChange,
    handleBaseAccountSelect,
    handleManualEntryChange,
    hasBaseAccount: Boolean(baseAccountId),
    isAwaitingConfirmation,
    isCreating,
    isManualEntryEnabled,
    isProcessing,
    isSaveDisabled,
    natures,
    selectedBaseAccountId: baseAccountId || undefined,
    submitForm,
  }
}
